import React, { useState, useCallback } from 'react';
import {
  CheckSquare,
  ListChecks,
  ToggleLeft,
  Type,
  ArrowLeftRight,
  FormInput,
  ChevronDown,
  GripVertical,
  FileText,
  Sparkles,
  Search,
  Crown,
  LucideIcon,
} from 'lucide-react';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import Modal from 'components/ui/Modal';
import type { QuestionType } from '@examing/types';
import { cn } from 'lib/utils';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';

// The five basic types are free on every plan; the rest are Growth+ (gated by
// FEATURES.ADVANCED_QUESTION_TYPES). Mirrors apps/api/src/common/question-types.ts.
const BASIC_QUESTION_TYPE_VALUES = new Set<QuestionType>([
  'MULTIPLE_CHOICE',
  'MULTIPLE_RESPONSE',
  'TRUE_FALSE',
  'SHORT_ANSWER',
  'MATCHING',
]);

interface QuestionTypeConfig {
  value: QuestionType;
  label: string;
  description: string;
  icon: LucideIcon;
  aiSupported?: boolean;
}

interface QuestionTypeCategory {
  name: string;
  description: string;
  types: QuestionTypeConfig[];
}

const QUESTION_TYPE_CATEGORIES: QuestionTypeCategory[] = [
  {
    name: 'Basic',
    description: 'Standard question formats',
    types: [
      {
        value: 'MULTIPLE_CHOICE',
        label: 'Multiple Choice',
        description: 'Single correct answer from options',
        icon: CheckSquare,
        aiSupported: true,
      },
      {
        value: 'MULTIPLE_RESPONSE',
        label: 'Multiple Response',
        description: 'Select all correct answers',
        icon: ListChecks,
        aiSupported: true,
      },
      {
        value: 'TRUE_FALSE',
        label: 'True/False',
        description: 'Binary choice question',
        icon: ToggleLeft,
        aiSupported: true,
      },
      {
        value: 'SHORT_ANSWER',
        label: 'Short Answer',
        description: 'Text response, exact match',
        icon: Type,
        aiSupported: true,
      },
      {
        value: 'MATCHING',
        label: 'Matching',
        description: 'Match pairs of items',
        icon: ArrowLeftRight,
        aiSupported: true,
      },
    ],
  },
  {
    name: 'Text-Based',
    description: 'Advanced text interactions',
    types: [
      {
        value: 'FILL_IN_BLANKS',
        label: 'Fill in the Blanks',
        description: 'Complete sentences with missing words',
        icon: FormInput,
        aiSupported: true,
      },
      {
        value: 'OPEN_ENDED',
        label: 'Open Ended',
        description: 'Extended text response',
        icon: FileText,
        aiSupported: true,
      },
    ],
  },
  {
    name: 'Ordering',
    description: 'Arrange items in a sequence',
    types: [
      {
        value: 'REORDER',
        label: 'Reorder',
        description: 'Arrange items in correct order',
        icon: GripVertical,
        aiSupported: true,
      },


    ],
  },
  {
    name: 'Composite & Special',
    description: 'Complex and interactive types',
    types: [
      {
        value: 'PASSAGE',
        label: 'Passage',
        description: 'Reading passage with sub-questions',
        icon: FileText,
        aiSupported: true,
      },
    ],
  },
];

interface QuestionTypePickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (type: QuestionType) => void;
  onGenerateWithAI?: (topic: string, types: QuestionType[]) => void;
}

export function QuestionTypePickerModal({
  isOpen,
  onClose,
  onSelect,
  onGenerateWithAI,
}: QuestionTypePickerModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [selectedAITypes, setSelectedAITypes] = useState<QuestionType[]>([]);

  // Advanced (non-basic) question types are a Growth+ capability.
  const canAdvancedTypes = useFeature(FEATURES.ADVANCED_QUESTION_TYPES);
  const openUpgrade = useOpenUpgradeForFeature();
  const isLockedType = useCallback(
    (type: QuestionType) => !canAdvancedTypes && !BASIC_QUESTION_TYPE_VALUES.has(type),
    [canAdvancedTypes],
  );

  const filteredCategories = QUESTION_TYPE_CATEGORIES.map((category) => ({
    ...category,
    types: category.types.filter(
      (type) =>
        type.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        type.description.toLowerCase().includes(searchQuery.toLowerCase())
    ),
  })).filter((category) => category.types.length > 0);

  const handleTypeSelect = useCallback(
    (type: QuestionType) => {
      if (isLockedType(type)) {
        openUpgrade(FEATURES.ADVANCED_QUESTION_TYPES);
        return;
      }
      onSelect(type);
      onClose();
    },
    [onSelect, onClose, isLockedType, openUpgrade]
  );

  const handleAITypeToggle = useCallback(
    (type: QuestionType) => {
      if (isLockedType(type)) {
        openUpgrade(FEATURES.ADVANCED_QUESTION_TYPES);
        return;
      }
      setSelectedAITypes((prev) =>
        prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
      );
    },
    [isLockedType, openUpgrade]
  );

  const handleGenerateWithAI = useCallback(() => {
    if (onGenerateWithAI && aiTopic.trim() && selectedAITypes.length > 0) {
      onGenerateWithAI(aiTopic.trim(), selectedAITypes);
      onClose();
    }
  }, [onGenerateWithAI, aiTopic, selectedAITypes, onClose]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Add Question" size="xl">
      <div className="question-type-picker">
        {/* AI Generation Panel */}
        {onGenerateWithAI && (
          <div className="mb-6 rounded-lg border border-purple-200 bg-purple-50/60 p-4">
            <button
              type="button"
              onClick={() => setShowAIPanel(!showAIPanel)}
              className="flex w-full items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                  <Sparkles size={20} className="text-purple-600" />
                </div>
                <div className="text-left">
                  <p className="font-medium text-purple-900">Generate with AI</p>
                  <p className="text-sm text-purple-600">
                    Automatically create questions on any topic
                  </p>
                </div>
              </div>
              <ChevronDown
                size={20}
                className={cn(
                  'text-purple-600 transition-transform',
                  showAIPanel && 'rotate-180'
                )}
              />
            </button>

            {showAIPanel && (
              <div className="mt-4 space-y-4 border-t border-purple-200 pt-4">
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Topic or Subject
                  </label>
                  <Input
                    value={aiTopic}
                    onChange={(e) => setAiTopic(e.target.value)}
                    placeholder="e.g., World War II, Photosynthesis, Algebra..."
                    className="border-purple-200 focus:border-purple-400"
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Question Types to Generate
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {QUESTION_TYPE_CATEGORIES.flatMap((cat) => cat.types)
                      .filter((type) => type.aiSupported)
                      .map((type) => {
                        const Icon = type.icon;
                        const isSelected = selectedAITypes.includes(type.value);
                        const locked = isLockedType(type.value);
                        return (
                          <button
                            key={type.value}
                            type="button"
                            onClick={() => handleAITypeToggle(type.value)}
                            title={locked ? 'Advanced type — a Growth feature' : undefined}
                            className={cn(
                              'flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm transition-colors',
                              isSelected
                                ? 'border-purple-500 bg-purple-100 text-purple-700'
                                : 'border-gray-200 bg-white text-gray-600 hover:border-purple-300'
                            )}
                          >
                            <Icon size={14} />
                            {type.label}
                            {locked && <Crown className="size-3 text-amber-500" aria-hidden />}
                          </button>
                        );
                      })}
                  </div>
                </div>

                <Button
                  onClick={handleGenerateWithAI}
                  disabled={!aiTopic.trim() || selectedAITypes.length === 0}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                >
                  <Sparkles size={16} className="mr-2" />
                  Generate Questions
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Search */}
        <div className="relative mb-4">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
          />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search question types..."
            className="pl-9"
          />
        </div>

        {/* Question Type Grid */}
        <div className="max-h-[60vh] space-y-6 overflow-y-auto pr-2">
          {filteredCategories.map((category) => (
            <div key={category.name}>
              <div className="mb-3">
                <h3 className="text-sm font-semibold text-gray-900">{category.name}</h3>
                <p className="text-xs text-gray-500">{category.description}</p>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {category.types.map((type) => {
                  const Icon = type.icon;
                  const locked = isLockedType(type.value);
                  return (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleTypeSelect(type.value)}
                      title={locked ? 'Advanced type — a Growth feature' : undefined}
                      className="group relative flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left transition-all hover:border-primary-600 hover:shadow-sm"
                    >
                      {locked && (
                        <span className="absolute right-2 top-2 flex size-5 items-center justify-center rounded-full bg-amber-400 text-amber-950 shadow-sm">
                          <Crown className="size-3" aria-hidden />
                        </span>
                      )}
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100 transition-colors group-hover:bg-primary/10">
                        <Icon size={20} className="text-gray-600 group-hover:text-primary-700" />
                      </div>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {type.label}
                        </p>
                        <p className="truncate text-xs text-gray-500">{type.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {filteredCategories.length === 0 && (
            <div className="py-12 text-center">
              <p className="text-sm text-gray-500">
                No question types match your search.
              </p>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default QuestionTypePickerModal;
